import { Link } from "react-router-dom";
import { LOGO_LIGHT } from "../lib/api";

/**
 * AuthShell — shared layout for Login / Signup / ForgotPassword / ResetPassword.
 *
 * Layout (designer brief):
 *   - Asymmetric 5/7 split on md+ screens (form left, hero image right).
 *   - Hero image hidden on mobile; form takes full width.
 *   - Glassmorphism quote card in bottom-left of hero image.
 *
 * Props:
 *   - heroImage  : URL of the right-side image (per page, sets the mood).
 *   - heroQuote  : Editorial quote string shown in the glass card.
 *   - heroAttrib : Optional small caption under the quote.
 *   - heading    : Page-specific H1 (e.g. "Welcome back.").
 *   - sub        : Sub-heading paragraph under the H1.
 *   - children   : The form + footer links.
 */
export default function AuthShell({ heroImage, heroQuote, heroAttrib, heading, sub, children, testid = "auth-shell" }) {
    return (
        <div className="min-h-screen bg-[#FAFAF9] grid md:grid-cols-12" data-testid={testid}>
            {/* LEFT — Form column (5/12) */}
            <div className="md:col-span-5 flex items-center justify-center p-6 md:p-12 lg:p-16 order-2 md:order-1">
                <div className="w-full max-w-md">
                    {/* Mobile logo (hero is hidden on mobile, so show brand mark inline) */}
                    <div className="md:hidden mb-10">
                        <Link to="/" className="flex items-center gap-3">
                            <img src={LOGO_LIGHT} alt="" className="h-11 w-11 rounded-full bg-stone-900 p-1" />
                            <span className="font-editorial text-xl">Energize.fit</span>
                        </Link>
                    </div>
                    {heading && (
                        <h1 className="font-editorial text-4xl md:text-5xl text-stone-900 mb-3 leading-[1.05] tracking-tight">
                            {heading}
                        </h1>
                    )}
                    {sub && (
                        <p className="text-stone-600 mb-10 leading-relaxed">{sub}</p>
                    )}
                    {children}
                </div>
            </div>

            {/* RIGHT — Hero image column (7/12) */}
            <div className="hidden md:block md:col-span-7 relative overflow-hidden order-1 md:order-2">
                <img
                    src={heroImage}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Subtle bottom gradient so the glass card reads cleanly */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

                {/* Logo top-left over image */}
                <Link to="/" className="absolute top-8 left-8 flex items-center gap-3 z-10" data-testid="auth-hero-logo">
                    <img src={LOGO_LIGHT} alt="" className="h-11 w-11 rounded-full bg-white/95 p-1 shadow-sm" />
                    <span className="font-editorial text-xl text-white drop-shadow-sm">Energize.fit</span>
                </Link>

                {/* Glassmorphism quote card — bottom-left, per design brief */}
                {heroQuote && (
                    <div
                        className="absolute bottom-10 left-10 right-10 max-w-md backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl p-7 shadow-2xl"
                        data-testid="auth-hero-quote"
                    >
                        <p className="font-editorial text-2xl lg:text-3xl text-white leading-snug">
                            &ldquo;{heroQuote}&rdquo;
                        </p>
                        {heroAttrib && (
                            <p className="mt-4 text-white/80 text-sm tracking-wide">{heroAttrib}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
